import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { VenueProvider, useVenue } from './context/VenueContext';
import BottomNav from './components/BottomNav';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import NuovaChiusuraPage from './pages/NuovaChiusuraPage';
import ChiusureListPage from './pages/ChiusureListPage';
import ChiusuraDetailPage from './pages/ChiusuraDetailPage';
import ImpostazioniPage from './pages/ImpostazioniPage';
import AnalyticsPage from './pages/AnalyticsPage';
import ImportPage from './pages/ImportPage';
import GestioneUtentiPage from './pages/GestioneUtentiPage';
import LocaliPage from './pages/LocaliPage';
import CambiaPasswordPage from './pages/CambiaPasswordPage';
import SelezioneLocalePage from './pages/SelezioneLocalePage';
import InfoPage from './pages/InfoPage';
import AssistentePage from './pages/AssistentePage';
import DashboardGruppoPage from './pages/DashboardGruppoPage';
import UsciteCassaPage from './pages/UsciteCassaPage';
import BilancioPage from './pages/BilancioPage';
import VendutoPage from './pages/VendutoPage';
import VendutoCaricamentoPage from './pages/VendutoCaricamentoPage';
import VendutoAnalisiPage from './pages/VendutoAnalisiPage';
import VendutoConfrontoPage from './pages/VendutoConfrontoPage';
import VendutoQueryPage from './pages/VendutoQueryPage';
import VendutoImpostazioniPage from './pages/VendutoImpostazioniPage';
import AcquistiPrezzarioPage from './pages/acquisti/AcquistiPrezzarioPage';
import AcquistiDashboardPage from './pages/acquisti/AcquistiDashboardPage';
import AcquistiFornitoriPage from './pages/acquisti/AcquistiFornitoriPage';
import AcquistiFornitorePage from './pages/acquisti/AcquistiFornitorePage';
import AcquistiPersonalePage from './pages/acquisti/AcquistiPersonalePage';
import AcquistiCostiFissiPage from './pages/acquisti/AcquistiCostiFissiPage';
import AcquistiBancariPage from './pages/acquisti/AcquistiBancariPage';
import AcquistiProdottoPage from './pages/acquisti/AcquistiProdottoPage';
import AcquistiDocumentiPage from './pages/acquisti/AcquistiDocumentiPage';
import AcquistiCaricamentoPage from './pages/acquisti/AcquistiCaricamentoPage';
import PromemoriaPage from './pages/PromemoriaPage';
import './App.css';

/**
 * Wrapper di accesso protetto.
 *
 * Politiche di redirect (in ordine):
 *  1. Non autenticato → /login
 *  2. Autenticato ma must_change_password=true e non sta visitando /cambia-password → /cambia-password
 *  3. Autenticato senza locale attivo (anche super_admin) e non sta visitando una pagina "venue-less" → /seleziona-locale
 *  4. Tutto ok → renderizza children + BottomNav
 *
 * `allowOnForcedPasswordChange` consente l'accesso alla pagina cambio password.
 * `venueLess` (default false): pagine che NON richiedono un locale attivo
 *   (es. /seleziona-locale, /impostazioni, /locali, /gestione-utenti, /cambia-password).
 */
const ProtectedRoute = ({ children, allowOnForcedPasswordChange = false, venueLess = false }) => {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const { activeVenue, availableVenues, loading: venueLoading, noVenues } = useVenue();
  const location = useLocation();

  if (authLoading || venueLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  // Forced password change ha priorità su tutto
  if (
    user?.must_change_password &&
    !allowOnForcedPasswordChange &&
    location.pathname !== '/cambia-password'
  ) {
    return <Navigate to="/cambia-password" replace />;
  }

  // Locale non selezionato: se la pagina non è venueLess, redirect a selezione
  if (!venueLess && !activeVenue) {
    // Utente non super_admin senza locali → /seleziona-locale (mostra "nessun locale")
    // Utente con N>=2 locali → /seleziona-locale (picker)
    // Utente super_admin → /seleziona-locale (picker su tutti)
    return <Navigate to="/seleziona-locale" replace />;
  }

  // Se è venueLess e utente non super_admin e non ha locali → consenti accesso solo a /seleziona-locale
  if (venueLess && noVenues && !user?.is_super_admin && location.pathname !== '/seleziona-locale' && location.pathname !== '/cambia-password') {
    return <Navigate to="/seleziona-locale" replace />;
  }

  return (
    <>
      {children}
      {/* Niente BottomNav sulla pagina di selezione locale per non distrarre */}
      {!venueLess && <BottomNav />}
    </>
  );
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />

      <Route path="/seleziona-locale" element={<ProtectedRoute venueLess><SelezioneLocalePage /></ProtectedRoute>} />
      <Route path="/cambia-password" element={<ProtectedRoute allowOnForcedPasswordChange venueLess><CambiaPasswordPage /></ProtectedRoute>} />

      {/* Pagine venue-less: gestione globale (admin/super_admin) */}
      <Route path="/impostazioni" element={<ProtectedRoute venueLess><ImpostazioniPage /></ProtectedRoute>} />
      <Route path="/gestione-utenti" element={<ProtectedRoute venueLess><GestioneUtentiPage /></ProtectedRoute>} />
      <Route path="/locali" element={<ProtectedRoute venueLess><LocaliPage /></ProtectedRoute>} />
      <Route path="/dashboard-gruppo" element={<ProtectedRoute venueLess><DashboardGruppoPage /></ProtectedRoute>} />
      <Route path="/info" element={<ProtectedRoute venueLess><InfoPage /></ProtectedRoute>} />
      <Route path="/assistente" element={<ProtectedRoute><AssistentePage /></ProtectedRoute>} />

      {/* Pagine operative: richiedono un locale attivo */}
      <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/nuova-chiusura" element={<ProtectedRoute><NuovaChiusuraPage /></ProtectedRoute>} />
      <Route path="/chiusure" element={<ProtectedRoute><ChiusureListPage /></ProtectedRoute>} />
      <Route path="/chiusure/:id" element={<ProtectedRoute><ChiusuraDetailPage /></ProtectedRoute>} />
      <Route path="/chiusure/:id/modifica" element={<ProtectedRoute><NuovaChiusuraPage /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/import" element={<ProtectedRoute><ImportPage /></ProtectedRoute>} />
      <Route path="/uscite" element={<ProtectedRoute><UsciteCassaPage /></ProtectedRoute>} />
      <Route path="/bilancio" element={<ProtectedRoute><BilancioPage /></ProtectedRoute>} />
      <Route path="/promemoria" element={<ProtectedRoute><PromemoriaPage /></ProtectedRoute>} />

      {/* Controllo di Gestione (venduto Cassa in Cloud) */}
      <Route path="/venduto" element={<ProtectedRoute><VendutoPage /></ProtectedRoute>} />
      <Route path="/venduto/caricamento" element={<ProtectedRoute><VendutoCaricamentoPage /></ProtectedRoute>} />
      <Route path="/venduto/analisi" element={<ProtectedRoute><VendutoAnalisiPage /></ProtectedRoute>} />
      <Route path="/venduto/confronto" element={<ProtectedRoute><VendutoConfrontoPage /></ProtectedRoute>} />
      <Route path="/venduto/query" element={<ProtectedRoute><VendutoQueryPage /></ProtectedRoute>} />
      <Route path="/venduto/impostazioni" element={<ProtectedRoute><VendutoImpostazioniPage /></ProtectedRoute>} />

      {/* Acquisti e Costi (fatture passive) */}
      <Route path="/acquisti" element={<ProtectedRoute><AcquistiPrezzarioPage /></ProtectedRoute>} />
      <Route path="/acquisti/dashboard" element={<ProtectedRoute><AcquistiDashboardPage /></ProtectedRoute>} />
      <Route path="/acquisti/fornitori" element={<ProtectedRoute><AcquistiFornitoriPage /></ProtectedRoute>} />
      <Route path="/acquisti/fornitori/:id" element={<ProtectedRoute><AcquistiFornitorePage /></ProtectedRoute>} />
      <Route path="/acquisti/personale" element={<ProtectedRoute><AcquistiPersonalePage /></ProtectedRoute>} />
      <Route path="/acquisti/costi-fissi" element={<ProtectedRoute><AcquistiCostiFissiPage /></ProtectedRoute>} />
      <Route path="/acquisti/bancari" element={<ProtectedRoute><AcquistiBancariPage /></ProtectedRoute>} />
      <Route path="/acquisti/prodotti/:id" element={<ProtectedRoute><AcquistiProdottoPage /></ProtectedRoute>} />
      <Route path="/acquisti/documenti" element={<ProtectedRoute><AcquistiDocumentiPage /></ProtectedRoute>} />
      <Route path="/acquisti/carica" element={<ProtectedRoute><AcquistiCaricamentoPage /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <VenueProvider>
          <div className="App min-h-screen bg-background grain-texture">
            <AppRoutes />
            <Toaster position="top-center" richColors />
          </div>
        </VenueProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
